import {
	subscribeToPrivateBubble,
	unsubscribeFromChannel,
	getPusherInstance,
	isSubscriptionPending,
	getPendingSubscription,
	bindChannelEventWithChunking,
} from '@/helpers/pusher'
import { setMessageAndMoveCursor, getMessage, getChat } from '@/store'
import { createLogger } from '@/helpers/log'

const log = createLogger('ThreadPreviewPusher')

const UNSUBSCRIBE_DELAY = 5000

// Map<threadId, { channel, channelName, refCount, unsubTimer, callbacks: Set }>
const subscriptions = new Map()

function handleMessageAdded(threadId, message) {
	const sub = subscriptions.get(threadId)
	if (!sub) return

	setMessageAndMoveCursor(message)

	// Read back the normalized message from the store
	const normalized = getMessage(message.id || message.uuid)
	if (!normalized || normalized.systemevent) return

	for (const cb of sub.callbacks) {
		cb(normalized)
	}
}

function connectChannel(threadId) {
	// If another consumer (e.g. ChatPanel) already has this channel
	// subscribed, don't call subscribeToPrivateBubble — it would
	// channel.unbind() and wipe their handlers. Just store the
	// reference and rely on store reactivity for display updates.
	const chat = getChat(threadId)
	const channelcode =
		getChat(chat?.parent_chat_id)?.channelcode || chat?.channelcode
	if (channelcode) {
		const channelName = `private-bubble.${threadId}.${channelcode}`
		const existingChannel = getPusherInstance()?.channel(channelName)
		if (existingChannel?.subscribed) {
			const currentSub = subscriptions.get(threadId)
			if (currentSub && currentSub.refCount > 0) {
				currentSub.channel = existingChannel
				currentSub.channelName = channelName
				log('channel already active, piggybacking', threadId)
			}
			return
		}
		if (isSubscriptionPending(channelName)) {
			log('channel pending, waiting for subscription', threadId)
			getPendingSubscription(channelName)
				?.then((channel) => {
					const currentSub = subscriptions.get(threadId)
					if (!currentSub || currentSub.refCount === 0) return
					currentSub.channel = channel
					currentSub.channelName = channel.name
					log('pending subscription resolved, piggybacking', threadId)
				})
				.catch((e) => {
					log(
						'pending subscription failed, retrying own subscribe',
						threadId,
						e
					)
					// Safe: the failed promise's .finally already cleared
					// pendingSubscriptions, so the retry takes the direct
					// subscribeToPrivateBubble path — no infinite loop.
					connectChannel(threadId)
				})
			return
		}
	}

	subscribeToPrivateBubble(threadId)
		.then((channel) => {
			const currentSub = subscriptions.get(threadId)
			if (!currentSub || currentSub.refCount === 0) {
				unsubscribeFromChannel(channel.name)
				return
			}
			currentSub.channel = channel
			currentSub.channelName = channel.name
			bindChannelEventWithChunking(
				channel,
				'MessageCreated',
				handleMessageAdded.bind(null, threadId)
			)
			log('subscribed to thread preview', threadId)
		})
		.catch((e) => {
			log('failed to subscribe to thread preview', threadId, e)
			const currentSub = subscriptions.get(threadId)
			if (currentSub && currentSub.refCount <= 0) {
				subscriptions.delete(threadId)
			}
		})
}

export function resubscribeThreadPreview(threadId) {
	const sub = subscriptions.get(threadId)
	if (!sub || sub.refCount <= 0) return

	// Unsubscribe from old channel
	if (sub.channelName) {
		unsubscribeFromChannel(sub.channelName)
		sub.channel = null
		sub.channelName = null
	}

	// Resubscribe with new channelcode (picked up from updated store)
	connectChannel(threadId)
}

export function getActiveThreadPreviewIds() {
	const ids = []
	for (const [threadId, sub] of subscriptions) {
		if (sub.refCount > 0) ids.push(threadId)
	}
	return ids
}

/**
 * Subscribe to thread messages via Pusher to keep the store up to date.
 * Callback is optional — consumers can rely on store reactivity instead.
 */
export function subscribeToThreadPreview(threadId, callback) {
	let sub = subscriptions.get(threadId)

	if (sub) {
		sub.refCount++
		if (callback) sub.callbacks.add(callback)
		if (sub.unsubTimer) {
			clearTimeout(sub.unsubTimer)
			sub.unsubTimer = null
		}
		// If the channel was killed externally (e.g. ChatPanel unload),
		// reconnect so new messages continue flowing into the store.
		if (sub.channel && !sub.channel.subscribed) {
			log('dead channel detected, reconnecting', threadId)
			sub.channel = null
			sub.channelName = null
			connectChannel(threadId)
		}
	} else {
		sub = {
			channel: null,
			channelName: null,
			refCount: 1,
			unsubTimer: null,
			callbacks: new Set(callback ? [callback] : []),
		}
		subscriptions.set(threadId, sub)
		connectChannel(threadId)
	}

	return () => {
		const current = subscriptions.get(threadId)
		if (!current) return

		if (callback) current.callbacks.delete(callback)
		current.refCount--

		if (current.refCount <= 0) {
			current.unsubTimer = setTimeout(() => {
				const entry = subscriptions.get(threadId)
				if (!entry || entry.refCount > 0) return
				if (entry.channelName) {
					unsubscribeFromChannel(entry.channelName)
					log('unsubscribed from thread preview', threadId)
				}
				subscriptions.delete(threadId)
			}, UNSUBSCRIBE_DELAY)
		}
	}
}
